import { Box, Flex, Heading, Select, Text } from "@radix-ui/themes";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "../../../lib/supabase";
import { ProofingList } from "./index";

interface RepOption {
  id: string;
  full_name: string;
}

const VIEW_AS_LOCATIONS = [
  { label: "Montréal", lat: 45.5017, lng: -73.5673 },
  { label: "Québec city", lat: 46.8139, lng: -71.208 },
  { label: "Trois-Rivières", lat: 46.3432, lng: -72.5418 },
];

export function ViewAsWrapper() {
  const { t } = useTranslation();
  const [reps, setReps] = useState<RepOption[]>([]);
  const [selectedRepId, setSelectedRepId] = useState<string>("");
  const [selectedLocationIndex, setSelectedLocationIndex] =
    useState<string>("");

  // Fetch reps (lightweight query)
  useEffect(() => {
    async function fetchReps() {
      const { data, error } = await supabase
        .from("users")
        .select("id, full_name, user_roles!inner(role)")
        .eq("user_roles.role", "rep")
        .is("deleted_at", null)
        .order("full_name", { ascending: true });

      if (error) {
        console.error("Error fetching reps:", error);
        return;
      }

      setReps(
        (data ?? []).map((r: { id: string; full_name: string }) => ({
          id: r.id,
          full_name: r.full_name,
        })),
      );
    }

    fetchReps();
  }, []);

  const selectedLocation =
    selectedLocationIndex !== ""
      ? VIEW_AS_LOCATIONS[Number(selectedLocationIndex)]
      : null;

  const position =
    selectedLocation != null
      ? { latitude: selectedLocation.lat, longitude: selectedLocation.lng }
      : undefined;

  const bothSelected = selectedRepId !== "" && position != null;

  return (
    <Box
      style={{
        height: "100%",
        overflowY: "auto",
      }}
    >
      <Flex
        direction="column"
        gap="3"
        p="4"
        mx="auto"
        style={{ maxWidth: 800 }}
      >
        <Heading size="4">{t("proofing.viewAs.title")}</Heading>

        <Flex direction="column" gap="2">
          <Text size="2" weight="bold">
            {t("proofing.viewAs.selectRep")}
          </Text>
          <Select.Root value={selectedRepId} onValueChange={setSelectedRepId}>
            <Select.Trigger
              placeholder={t("proofing.viewAs.repPlaceholder")}
              style={{ width: "100%" }}
            />
            <Select.Content>
              {reps.map((rep) => (
                <Select.Item key={rep.id} value={rep.id}>
                  {rep.full_name}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
        </Flex>

        <Flex direction="column" gap="2">
          <Text size="2" weight="bold">
            {t("proofing.viewAs.selectLocation")}
          </Text>
          <Select.Root
            value={selectedLocationIndex}
            onValueChange={setSelectedLocationIndex}
          >
            <Select.Trigger
              placeholder={t("proofing.viewAs.locationPlaceholder")}
              style={{ width: "100%" }}
            />
            <Select.Content>
              {VIEW_AS_LOCATIONS.map((loc, index) => (
                <Select.Item key={loc.label} value={String(index)}>
                  {loc.label}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
        </Flex>

        {bothSelected && (
          <ProofingList userId={selectedRepId} position={position} viewAsMode />
        )}
      </Flex>
    </Box>
  );
}
